import React, { useState } from "react";
import { 
  IconCreditCard, 
  IconShield, 
  IconLock, 
  IconCheck, 
  IconStar,
  IconCurrency,
  IconGift,
  IconSparkles
} from '@tabler/icons-react';
import axios from "axios";
import { BASE_URL } from "../apis/api";

function Payment() {
    
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const displayRazorpay = async () => {
    setIsProcessing(true);
    
    const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!res) {
      alert("Razorpay SDK failed to load.");
      setIsProcessing(false);
      return;
    }

    const result = await axios.post(BASE_URL + "/api/payment/create-order", {
      amount: 1,
      id: "order_" + Math.random().toString(36).substr(2, 9),
      currency: "USD"
    });

    const { amount, id: order_id, currency } = result.data;
    
    const options = {
      key: "rzp_live_CZcDMAbMA05zPD",
      amount,
      currency,
      name: "Solmarine Tech",
      description: "Premium Purchase",
      order_id,
      handler: function (response) {
        setIsProcessing(false);
        setPaymentSuccess(true);
        setTimeout(() => setPaymentSuccess(false), 3000);
      },
      prefill: {
        name: "Solmarin Tech User",
        email: "info@solmarinetech.com",
        contact: "7012034152",
      },
      theme: {
        color: "#528FF0",
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
    
    paymentObject.on('payment.failed', () => setIsProcessing(false));
  };

  const features = [
    { icon: IconShield, text: "256-bit SSL Encryption" },
    { icon: IconLock, text: "Secure Payment Gateway" },
    { icon: IconCheck, text: "Instant Transaction" },
    { icon: IconStar, text: "Premium Experience" }
  ];

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-white/50">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <IconCheck className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-4">Thank you for your purchase</p>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-green-800 font-semibold">Transaction completed successfully</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Payment Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-white/50">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <IconSparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">Solmarine Tech</h1>
                    <p className="text-white/80 text-sm">Secure Payment</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">Amount</p>
                  <p className="text-2xl font-bold">$1.00</p>
                </div>
              </div>
            </div>

            <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-purple-300/20 rounded-full blur-lg"></div>
          </div>

          {/* Payment Content */}
          <div className="p-6 space-y-6">
            {/* Product Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                  <IconGift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Premium Feature Access</h3>
                  <p className="text-gray-600 text-sm">Unlock exclusive premium features</p>
                </div>
              </div>
            </div>

            {/* Security Features */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                  <feature.icon className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-700 font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Payment Methods */}
            <div className="bg-gray-50 rounded-2xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <IconCreditCard className="w-5 h-5 mr-2 text-blue-600" />
                Accepted Payment Methods
              </h4>
              <div className="flex space-x-2">
                {['Visa', 'MC', 'UPI', 'Wallet'].map((method, index) => (
                  <div key={index} className="bg-white rounded-lg px-3 py-2 shadow-sm border">
                    <span className="text-xs font-medium text-gray-700">{method}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pay Button */}
            <button
              onClick={displayRazorpay}
              disabled={isProcessing}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                isProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white'
              }`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <IconCreditCard className="w-6 h-6" />
                  <span>Pay $1.00 Now</span>
                </div>
              )}
            </button>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center space-x-4 pt-2">
              <div className="flex items-center space-x-1">
                <IconShield className="w-4 h-4 text-green-600" />
                <span className="text-xs text-gray-600">SSL Secured</span>
              </div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <IconLock className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-gray-600">Bank Grade Security</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Powered by{' '}
            <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Razorpay
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Payment;